import React, { useState } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card'
import { Badge } from './ui/badge'
import { Button } from './ui/button'
import { Textarea } from './ui/textarea'
import { useLanguage } from '../contexts/LanguageContext'
import { agentService, Agent } from '../services/api'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { 
  MessageSquare, 
  Edit, 
  Trash2, 
  Play, 
  Pause, 
  Settings,
  Plus,
  Bot,
  Activity,
  Clock,
  FileText,
  Send,
  Eye,
  AlertCircle
} from 'lucide-react'

interface AgentListProps {
  agents: Agent[]
  onEdit?: (agent: Agent) => void
  onDelete?: (agent: Agent) => void
  onToggleStatus?: (agent: Agent) => void
}

const AgentList: React.FC<AgentListProps> = ({ 
  agents, 
  onEdit, 
  onDelete, 
  onToggleStatus
}) => {
  const { t } = useLanguage()
  const navigate = useNavigate()
  const [prompt, setPrompt] = useState("")
  const [isCreatingSession, setIsCreatingSession] = useState(false)

  const handlePromptSubmit = async () => {
    if (prompt.trim()) {
      try {
        setIsCreatingSession(true)
        
        // 检查用户是否已登录
        const token = localStorage.getItem("token")
        if (!token) {
          // 如果未登录，保存用户输入到localStorage并跳转到登录页面
          localStorage.setItem('pending_prompt', prompt.trim())
          navigate("/login")
          return
        }
        
        // 调用API创建新session
        console.log('开始创建session，参数:', {
          prompt: prompt.trim()
        })
        
        const session = await agentService.createNewSession({
          prompt: prompt.trim()
        })
        
        console.log('创建session成功:', session)
        
        // 创建成功后跳转到agent页面
        if (session && session.sessionId) {
          // 同时传递sessionId和用户输入，这样agent页面可以显示用户输入并自动发送
          navigate(`/agent?sessionId=${session.sessionId}&input=${encodeURIComponent(prompt.trim())}`)
        } else {
          throw new Error('创建session失败：未获取到sessionId')
        }
      } catch (error) {
        console.error('创建session失败:', error)
        toast.error('创建会话失败，请稍后重试')
      } finally {
        setIsCreatingSession(false)
      }
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
      case 'NORMAL':
        return 'bg-green-100 text-green-800'
      case 'inactive':
        return 'bg-gray-100 text-gray-800'
      case 'draft':
        return 'bg-yellow-100 text-yellow-800'
      case 'error':
        return 'bg-red-100 text-red-800'
      case 'QUEUED':
        return 'bg-blue-100 text-blue-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const getStatusText = (status: string) => {
    switch (status) {
      case 'active':
        return t('agent.status.active') || 'Active'
      case 'NORMAL':
        return t('agent.status.active') || 'Active'
      case 'inactive':
        return t('agent.status.inactive') || 'Inactive'
      case 'draft':
        return t('agent.status.draft') || 'Draft'
      case 'error':
        return t('agent.status.error') || 'Error'
      case 'QUEUED':
        return 'Queued'
      default:
        return status
    }
  }

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'conversation':
        return <MessageSquare className="h-4 w-4" />
      case 'file_conversion':
        return <FileText className="h-4 w-4" />
      case 'image_generation':
        return <Bot className="h-4 w-4" />
      case 'code_assistant':
        return <Settings className="h-4 w-4" />
      default:
        return <Bot className="h-4 w-4" />
    }
  }

  const getTypeText = (type: string) => {
    switch (type) {
      case 'conversation':
        return t('home.conversationType') || 'Conversation'
      case 'file_conversion':
        return t('home.fileConversionType') || 'File Conversion'
      case 'image_generation':
        return t('home.imageGenerationType') || 'Image Generation'
      case 'code_assistant':
        return t('home.codeAssistantType') || 'Code Assistant'
      default:
        return type
    }
  }

  const formatStatusColor = (status: string) => {
    switch (status) {
      case 'QUEUED':
        return 'text-blue-500'
      case 'FAILED':
        return 'text-red-500'
    }
  }
  const formatDate = (dateString: string | number) => {
    if (typeof dateString === 'number') {
      return new Date(dateString).toLocaleDateString()
    }
    return new Date(dateString).toLocaleDateString()
  }

  if (agents.length === 0) {
    return (
      <div className="text-center py-12">
        <Bot className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-4 text-lg font-medium text-gray-900">
          {t('agent.noAgents') || 'No Agents Yet'}
        </h3>
        <p className="mt-2 text-sm text-gray-500">
          {t('agent.noAgentsDesc') || 'Create your first AI agent to get started'}
        </p>
        
        {/* Prompt Input for Empty State */}
        <div className="max-w-2xl mx-auto mt-6">
          <div className="relative">
            <Textarea
              placeholder={t('home.promptPlaceholder') || 'Describe the AI application you want to create...'}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              className="w-full text-base py-3 pr-16 min-h-[100px] border-2 border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 rounded-xl shadow-sm resize-none"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && e.ctrlKey) {
                  e.preventDefault()
                  handlePromptSubmit()
                }
              }}
              rows={4}
            />
            <Button 
              onClick={handlePromptSubmit}
              size="sm"
              className="absolute right-3 bottom-3 bg-blue-800 hover:bg-blue-900 h-8 w-8 p-0 rounded-lg shadow-md transition-all duration-200 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={!prompt.trim() || isCreatingSession}
            >
              {isCreatingSession ? (
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
              ) : (
                <Send className="h-3 w-3" />
              )}
            </Button>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            {t('home.promptShortcut') || 'Press Ctrl+Enter to submit quickly'}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Agents Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {agents.map((agent) => (
          <Card key={agent.id} className="hover:shadow-md transition-shadow">
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div className="flex items-center space-x-2">
                  {getTypeIcon(agent.type || '')}
                  <div>
                    <CardTitle className="text-base">{agent.name}</CardTitle>
                    <Badge variant="outline" className="text-xs">
                      {getTypeText(agent.type || '')}
                    </Badge>
                  </div>
                </div>
                <Badge className={`text-xs ${getStatusColor(agent.agentStatus || '')}`}>
                  {getStatusText(agent.agentStatus || '')}
                </Badge>
              </div>
            </CardHeader>
            
            <CardContent className="pt-0">
              <CardDescription className="text-sm mb-4 h-20 overflow-hidden text-ellipsis line-clamp-4">
                {agent.description}
              </CardDescription>

              {/* Agent Stats */}
              <div className="space-y-2 mb-4">
                {agent.usageCount !== undefined && (
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <span className="flex items-center">
                      <Activity className="mr-1 h-3 w-3" />
                      {t('agent.usageCount') || 'Usage'}
                    </span>
                    <span>{agent.usageCount}</span>
                  </div>
                )}
                
                {agent.lastUsed && (
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <span className="flex items-center">
                      <Clock className="mr-1 h-3 w-3" />
                      {t('agent.lastUsed') || 'Last Used'}
                    </span>
                    <span>{formatDate(agent.lastUsed)}</span>
                  </div>
                )}

                {agent.gmtCreated && (
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <span className="flex items-center">
                      <Clock className="mr-1 h-3 w-3" />
                      Created
                    </span>
                    <span>{formatDate(agent.gmtCreated)}</span>
                  </div>
                )}
                {agent.agentStatus && (
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <span className={`flex items-center ${formatStatusColor(agent.agentStatus)}`}>
                      {agent.agentStatus == 'QUEUED' && (
                        <Clock className="mr-1 h-3 w-3" />
                      )}
                      {agent.agentStatus == 'FAILED' && (
                        <AlertCircle className="mr-1 h-3 w-3 text-red-500" />
                      )}
                      Stage
                    </span>
                    <span className={formatStatusColor(agent.agentStatus)}>{agent.agentStatus}</span>
                  </div>
                )}
              </div>

              {/* Tags */}
              {agent.tags && agent.tags.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-4">
                  {agent.tags.slice(0, 3).map((tag, index) => (
                    <Badge key={index} variant="secondary" className="text-xs">
                      {tag}
                    </Badge>
                  ))}
                  {agent.tags.length > 3 && (
                    <Badge variant="secondary" className="text-xs">
                      +{agent.tags.length - 3}
                    </Badge>
                  )}
                </div>
              )}

              {/* Actions */}
              
              <div className="flex items-center justify-between pt-2 border-t">
                <div className="flex space-x-1">
                <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => navigate(`/agent/${agent.id}`)}
                      className="h-8 px-2"
                    >
                      <Eye className="h-3 w-3" />
                </Button>
                
                {agent.agentStatus && ['RUNNING', 'NORMAL'].includes(agent.agentStatus) && (
                    <>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onEdit?.(agent)}
                      className="h-8 px-2"
                    >
                      <Edit className="h-3 w-3" />
                    </Button>
                    
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onToggleStatus?.(agent)}
                      className="h-8 px-2"
                    >
                      {(agent.status === 'active' || agent.status === 'NORMAL') ? (
                        <Pause className="h-3 w-3" />
                      ) : (
                        <Play className="h-3 w-3" />
                      )}
                    </Button>
                    
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onDelete?.(agent)}
                      className="h-8 px-2 text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button></>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  )
}

export default AgentList 